// @flow

import React, { useEffect, useMemo, useRef, useState } from "react";
import { localize } from "redux-i18n";

import type { AbsenceReviewAppeal } from "@modules/shared/reducer/absence-review/types";
import { Button, ConfirmationModal, SchemaForm, View } from "@components";

import { getSchema } from "./schema";
import { getUiSchema } from "./ui-schema";

type Props = PropsFromLocalize & {
  submittedAppeal: ?AbsenceReviewAppeal,
  outcomeReasons: Array<Object>,
  clientId: number,
  employeeId: number,
  taskId: number,
  resolveAppeal: Function,
  updateAppeal: Function,
  onClose: Function,
};

const CANCELABLE_APPEALS = ["SUBMITTED", "UPHELD", "NOT_UPHELD"];

const HearingForm = (props: Props) => {
  const { t, submittedAppeal, outcomeReasons, onClose, clientId } = props;
  const formRef = useRef(null);
  const [confirModalOpen, setConfirmModalOpen] = useState(false);
  const selectedEmployeesRef = useRef({ hearingChairedBy: null, hrRepresentative: null });

  const schema = getSchema({ t, outcomeReasons });
  const uiSchema = getUiSchema({
    t,
    clientId,
    onSelectChairedBy: option => {
      selectedEmployeesRef.current.hearingChairedBy = option;
    },
    onSelectHrRepresentative: option => {
      selectedEmployeesRef.current.hrRepresentative = option;
    },
    fileNames: (submittedAppeal?.appealHearing?.documents || []).map(d => d.fileName),
  });

  const formData = useMemo(() => {
    const hearing = submittedAppeal?.appealHearing;
    if (hearing) {
      const matchedOutcome = outcomeReasons.find(r => r.name === hearing.rationale);

      return {
        hearingDate: hearing.hearingDate,
        hearingChairedBy: hearing.hearingChairedBy,
        hrRepresentative: hearing.hrRepresentative,
        ...(hearing.appealOutcome ? { appealOutcome: hearing.appealOutcome } : {}),
        ...(matchedOutcome ? { rationale: { id: matchedOutcome.id } } : {}),
        documents: (hearing?.documents || []).map(d => d.url),
      };
    }
    return null;
  }, [submittedAppeal, outcomeReasons]);

  const pickEmployeeFields = employee => {
    if (!employee || typeof employee !== "object") return employee;
    return {
      id: employee.id,
      employee_ref_primary: employee.employeeRefPrimary,
      first_name: employee.firstName,
      last_name: employee.lastName,
      name: `${employee.firstName} ${employee.lastName}`,
      work_email: employee.contactDetails?.workEmail,
    };
  };

  const handleSubmit = data => {
    const { hearingChairedBy, hrRepresentative, rationale, documents, ...rest } = data;
    const hearing = {
      ...rest,
      documents,
      hearingChairedBy: pickEmployeeFields(selectedEmployeesRef.current.hearingChairedBy),
      hrRepresentative: pickEmployeeFields(selectedEmployeesRef.current.hrRepresentative),
      rationale: rationale?.id ? outcomeReasons.find(r => r.id === rationale.id)?.name : null,
    };

    props.resolveAppeal({
      clientId: props.clientId,
      employeeId: props.employeeId,
      taskId: props.taskId,
      appealId: submittedAppeal?.id,
      hearing,
    });
  };

  const submitForm = () => {
    formRef.current.submit();
  };

  const toggleCancelAppeal = () => {
    setConfirmModalOpen(prev => !prev);
  };

  const handleCancelAppeal = () => {
    props.updateAppeal({
      clientId: props.clientId,
      employeeId: props.employeeId,
      taskId: props.taskId,
      appealId: submittedAppeal?.id,
      appeal: {
        id: submittedAppeal?.id,
        dateReceived: submittedAppeal?.dateReceived,
        reasonForAppeal: submittedAppeal?.reasonForAppeal,
        additionalNotes: submittedAppeal?.additionalNotes,
        status: "CANCELED",
      },
    });
    setConfirmModalOpen(prev => !prev);
    onClose();
  };

  return (
    <View gap={3}>
      <SchemaForm
        ref={formRef}
        schema={schema}
        formData={formData}
        onSubmit={handleSubmit}
        uiSchema={uiSchema}
        disabled={submittedAppeal?.status !== "SUBMITTED"}
        externalSubmit
      />
      <View direction="row" justifyContent="space-between">
        {CANCELABLE_APPEALS.includes(submittedAppeal?.status) ? (
          <Button materialIcon="cancel" onClick={toggleCancelAppeal} className="negative-outline">
            {t("CANCEL_APPEAL")}
          </Button>
        ) : (
          <div />
        )}
        <Button
          materialIcon="add_box"
          onClick={submitForm}
          disabled={submittedAppeal?.status !== "SUBMITTED"}
        >
          {t("SUBMIT")}
        </Button>
      </View>
      {confirModalOpen && (
        <ConfirmationModal
          headerText={t("CANCEL_APPEAL")}
          bodyContent={t("APPEAL_DELETION_COFIRMATION")}
          confirmBtnLabel={t("CANCEL_APPEAL_COFIRM")}
          cancelBtnLabel={t("KEEP_APPEAL")}
          onCancel={toggleCancelAppeal}
          onConfirm={handleCancelAppeal}
        />
      )}
    </View>
  );
};

export default localize()(HearingForm);
